import { useState, useEffect, memo, useCallback } from "react";
import { CSVLink } from "react-csv";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import { obtenerPQRS } from "../../functions/consultas";
import BotonSOS from "./sos/boton_sos";
import { crearRegistro } from "../../functions/crud";
import { subirArchivoFirebase } from "../../functions/archivos_firebase";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

const columnas = [
  { label: "Id",                    key: "id" },
  { label: "Fecha",                 key: "fecha" },
  { label: "Dirección",             key: "casa" },
  { label: "Usuario",               key: "residente" },
  { label: "Correo electrónico",    key: "correo" },
  { label: "Asunto",                key: "asunto" },
  { label: "Descripción del mensaje", key: "mensaje" },
  { label: "Estado",                key: "estado" },
  { label: "Foto",                  key: "url_foto1" }
];

const formatearFecha = (fecha) => fecha?.split(" ")[0] || "";

const transformarDatos = (data) =>
  Object.entries(data || {}).map(([id, item]) => ({
    id,
    ...item,
    fecha: formatearFecha(item.fecha)
  }));

const titulos = columnas.map(c => c.label.toUpperCase());
const campos  = columnas.map(c => c.key);

const esResidente = () => localStorage.getItem("Permisos") === "RESIDENTE";

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const ListadoPqrs = memo(() => {
  const [datos, setDatos]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState(null);

  const capturarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const data = await obtenerPQRS();
      let lista = transformarDatos(data);

      if (localStorage.getItem("Permisos") === "RESIDENTE") {
        const direccionResidente = (localStorage.getItem("Direccion") || "").trim().toUpperCase();
        lista = lista.filter(item => (item.casa || "").trim().toUpperCase() === direccionResidente);
      }

      setDatos(lista);
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  const crearPqrs = useCallback(async () => {
    const soloResidente = esResidente();
    const direccion = localStorage.getItem("Direccion") || "";
    const nombre    = localStorage.getItem("NombreCompleto") || "";
    const correo    = localStorage.getItem("CorreoElectronico") || "";

    const { value: formValues } = await Swal.fire({
      title: "Nueva PQRS",
      html: `
        <style>
          #pqrs-form .swal2-input, #pqrs-form .swal2-select, #pqrs-form .swal2-textarea {
            margin:0 !important; width:100% !important; box-sizing:border-box !important;
            padding:8px 10px !important; border:1px solid #e2e6ea !important;
            border-radius:6px !important; font-size:13px !important; color:#2c3e50 !important;
          }
          #pqrs-form .swal2-textarea { min-height:80px !important; resize:vertical !important; }
          #pqrs-form label { display:block; margin-bottom:4px; color:#6b7280; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.5px; }
          #pqrs-form .field { margin-bottom:10px; }
          #pqrs-form .row { display:flex; gap:10px; }
          #pqrs-form .row .field { flex:1; }
          #pqrs-form .readonly-val { background:#f8f9fb !important; color:#555 !important; cursor:default !important; }
        </style>
        <div id="pqrs-form" style="text-align:left;padding:0 4px;">
          <div class="row">
            <div class="field">
              <label>Dirección</label>
              <input id="pqrs-direccion" class="swal2-input ${soloResidente ? 'readonly-val' : ''}"
                value="${soloResidente ? direccion : ''}"
                placeholder="Dirección del residente"
                ${soloResidente ? 'readonly' : ''}>
            </div>
            <div class="field">
              <label>Residente</label>
              <input id="pqrs-nombre" class="swal2-input ${soloResidente ? 'readonly-val' : ''}"
                value="${soloResidente ? nombre : ''}"
                placeholder="Nombre del residente"
                ${soloResidente ? 'readonly' : ''}>
            </div>
          </div>
          <div class="field">
            <label>Asunto</label>
            <input id="pqrs-asunto" class="swal2-input" placeholder="Título breve del mensaje">
          </div>
          <div class="field">
            <label>Mensaje</label>
            <textarea id="pqrs-mensaje" class="swal2-textarea" placeholder="Describe la solicitud con detalle..."></textarea>
          </div>
          <div class="field">
            <label>Foto (opcional)</label>
            <input id="pqrs-foto" type="file" accept="image/*" class="swal2-input" style="padding:6px !important;">
            <div id="pqrs-preview" style="margin-top:8px;"></div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
      width: 560,
      didOpen: () => {
        const input = document.getElementById("pqrs-foto");
        input.addEventListener("change", () => {
          const archivo = input.files?.[0];
          document.getElementById("pqrs-preview").innerHTML = archivo
            ? `<img src="${URL.createObjectURL(archivo)}" style="width:80px;height:80px;object-fit:cover;border-radius:8px;border:2px solid #e0ddf8;" />`
            : "";
        });
      },
      preConfirm: () => {
        const dir     = document.getElementById("pqrs-direccion").value.trim();
        const nom     = document.getElementById("pqrs-nombre").value.trim();
        const asunto  = document.getElementById("pqrs-asunto").value.trim();
        const mensaje = document.getElementById("pqrs-mensaje").value.trim();
        if (!dir)     { Swal.showValidationMessage("La dirección es obligatoria"); return false; }
        if (!nom)     { Swal.showValidationMessage("El nombre del residente es obligatorio"); return false; }
        if (!asunto)  { Swal.showValidationMessage("El asunto es obligatorio"); return false; }
        if (!mensaje) { Swal.showValidationMessage("El mensaje es obligatorio"); return false; }
        const foto    = document.getElementById("pqrs-foto").files?.[0] || null;
        return { dir, nom, asunto, mensaje, foto };
      }
    });

    if (!formValues) return;

    try {
      const nit   = localStorage.getItem("CodigoCliente");
      const ahora = new Date();
      const fecha = `${ahora.getFullYear()}-${String(ahora.getMonth()+1).padStart(2,"0")}-${String(ahora.getDate()).padStart(2,"0")} ${String(ahora.getHours()).padStart(2,"0")}:${String(ahora.getMinutes()).padStart(2,"0")}`;

      let url_foto1 = "";
      if (formValues.foto) {
        Swal.fire({ title: "Guardando...", text: "Subiendo foto, por favor espere.", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const ext = (formValues.foto.name.split(".").pop() || "jpeg").toLowerCase();
        url_foto1 = await subirArchivoFirebase(`pqrs/${nit}/${ahora.toISOString().replace(/[:.]/g, "-")}foto_0.${ext}`, formValues.foto).catch((e) => {
          console.warn("[PQRS] No se pudo subir la foto:", e);
          return "";
        });
      }

      await crearRegistro(`coordi_ph/${nit}/pqrs`, {
        casa:      formValues.dir,
        residente: formValues.nom,
        correo:    soloResidente ? correo : "",
        asunto:    formValues.asunto,
        mensaje:   formValues.mensaje,
        estado:    "Pendiente",
        fecha,
        url_foto1
      });

      Swal.fire({ title: "PQRS enviada", text: "Tu solicitud fue registrada correctamente.", icon: "success", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire("Error", "No se pudo registrar la PQRS.", "error");
    }
  }, [capturarDatos]);

  const verRespuestasResidente = useCallback(async (fila) => {
    const { obtenerPQRS_Respuesta } = await import("../../functions/consultas");
    const data = await obtenerPQRS_Respuesta(fila.id).catch(() => null);
    const respuestas = Object.values(data || {});

    const contenido = respuestas.length === 0
      ? `<p style="color:#888;text-align:center;padding:20px 0;">Aún no hay respuestas para esta PQRS.</p>`
      : respuestas.map(r => `
          <div style="background:#f8f9fb;border-left:3px solid #6366f1;border-radius:6px;padding:12px 14px;margin-bottom:10px;">
            <p style="margin:0 0 6px;font-size:11px;color:#94a3b8;font-weight:600;">${r.fecha || ""}</p>
            <p style="margin:0;font-size:13px;color:#2c3e50;">${r.mensaje || ""}</p>
            ${r.url && r.url !== "No existe un documento anexado." ? `<a href="${r.url}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:8px;padding:8px 14px;background:#c0392b;color:#fff;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;"><i class="bi bi-file-earmark-pdf"></i> Ver documento anexo</a><br>` : ""}
            ${r.url_foto1 ? `<a href="${r.url_foto1}" target="_blank" rel="noreferrer"><img src="${r.url_foto1}" style="margin-top:8px;max-width:200px;border-radius:8px;" /></a>` : ""}
          </div>`).join("");

    Swal.fire({
      title: fila.asunto || "Respuestas",
      html: `<div style="text-align:left;max-height:360px;overflow-y:auto;padding:4px 2px;">${contenido}</div>`,
      confirmButtonText: "Cerrar",
      width: 560
    });
  }, []);

  const botonesAccion = [
    { texto: "Ver respuestas", icono: "bi-chat-left-text", accion: (fila) => {
        if (esResidente()) {
          verRespuestasResidente(fila);
          return;
        }
        localStorage.setItem("id_pqrs",          fila.id);
        localStorage.setItem("casa_pqrs",        fila.casa);
        localStorage.setItem("asunto_pqrs",      fila.asunto    || "Sin asunto");
        localStorage.setItem("residente_pqrs",   fila.residente || "");
        localStorage.setItem("mensaje_original", fila.mensaje   || "");
        localStorage.setItem("direccion_pqrs",   fila.casa      || "");
        localStorage.setItem("correo_pqrs",      fila.correo    || "");
        localStorage.setItem("fecha_pqrs",       fila.fecha     || "");
        window.open("/detalle_pqrs");
      }
    }
  ];

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  if (cargando) return <AnimacionCarga />;

  const datosFiltradosPorEstado = filtroEstado
    ? datos.filter(fila => (fila.estado || "Sin estado") === filtroEstado)
    : datos;

  return (
    <>
      <TablaGenerica
        datos={datosFiltradosPorEstado}
        titulos={titulos}
        campos={campos}
        elementosPorPagina={3000}
        columnaOrdenInicial={1}
        direccionOrdenInicial="desc"
        botones={botonesAccion}
        contenidoExtra={
          (() => {
            const conteo = datos.reduce((acc, fila) => {
              const estado = fila.estado || "Sin estado";
              acc[estado] = (acc[estado] || 0) + 1;
              return acc;
            }, {});
            return (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0", alignItems: "center" }}>
                {Object.entries(conteo).map(([estado, cantidad]) => (
                  <div
                    key={estado}
                    onClick={() => setFiltroEstado(prev => prev === estado ? null : estado)}
                    title="Clic para filtrar por este estado"
                    style={{
                      padding: "6px 14px", borderRadius: 20,
                      background: filtroEstado === estado ? "#0d6efd" : "#f0f0f0",
                      color: filtroEstado === estado ? "#fff" : "#000",
                      fontWeight: "bold", fontSize: 14,
                      cursor: "pointer", userSelect: "none",
                    }}
                  >
                    {estado}: {cantidad}
                  </div>
                ))}
                <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
                  <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
                  <CSVLink data={datosFiltradosPorEstado} headers={columnas} filename="listado_pqrs.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
                  <button onClick={crearPqrs} title="Nueva PQRS"><i className="bi bi-plus-lg" /></button>
                </div>
              </div>
            );
          })()
        }
      />

      <BotonSOS />
    </>
  );
});

export default ListadoPqrs;